import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Grid, Tabs, Tab, Typography, Chip, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Alert, CircularProgress, Paper, IconButton, Tooltip,
  Menu, MenuItem, ListItemIcon, ListItemText
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Calculate as CalcIcon,
  CheckCircle as LiquidarIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  ContentPaste as PasteIcon,
  PictureAsPdf as PdfIcon,
  Description as DocIcon,
  Receipt as ReceiptIcon,
  SelectAll as AllIcon,
  TableChart as ExcelIcon,
  Sailing as ViajesIcon,
} from '@mui/icons-material';
import { DataTable, TabPanel, ConfirmDialog } from '../shared';
import useNotification from '../../hooks/useNotification';
import { pescaService } from '../../services/pescaService';

const formatMoney = (val) => {
  if (val == null) return '-';
  return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(val);
};

const formatDate = (val) => {
  if (!val) return '-';
  if (val.length === 10) return val;
  return val.substring(0, 10);
};

const estadoColor = { BORRADOR: 'warning', LIQUIDADA: 'success', PAGADA: 'info' };

const LiquidacionDetalle = ({ liquidacion: liqInicial, rutEmpresa, onVolver, onActualizado }) => {
  const { showNotification } = useNotification();

  const [liq, setLiq] = useState(liqInicial);
  const [tabValue, setTabValue] = useState(0);
  const [cargos, setCargos] = useState([]);
  const [tripulantes, setTripulantes] = useState([]);
  const [naves, setNaves] = useState([]);

  // Dialog states
  const [dialog, setDialog] = useState({ open: false, tipo: null, data: null, editId: null });
  const [guardando, setGuardando] = useState(false);
  const [calculando, setCalculando] = useState(false);
  const [liquidando, setLiquidando] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, tipo: null, id: null });
  const [eliminando, setEliminando] = useState(false);

  // Descuentos dialog
  const [descDialog, setDescDialog] = useState({ open: false, dist: null });
  const [descData, setDescData] = useState({ anticipos: 0, seguros: 0, prestamos: 0 });
  const [guardandoDesc, setGuardandoDesc] = useState(false);

  // PDF menu
  const [pdfAnchor, setPdfAnchor] = useState(null);
  const [descargandoPdf, setDescargandoPdf] = useState(false);
  const [descargandoExcel, setDescargandoExcel] = useState(false);

  // Paste dialog
  const [pasteDialog, setPasteDialog] = useState({ open: false, tipo: null });
  const [pasteText, setPasteText] = useState('');
  const [pastePreview, setPastePreview] = useState([]);
  const [pegando, setPegando] = useState(false);

  const esBorrador = liq.estado === 'BORRADOR';

  // Load catalogs
  useEffect(() => {
    const cargar = async () => {
      try {
        const [c, t, n] = await Promise.all([
          pescaService.cargarCargos(rutEmpresa),
          pescaService.cargarTripulantes(rutEmpresa, false),
          pescaService.cargarNaves(rutEmpresa),
        ]);
        setCargos(c || []);
        setTripulantes(t || []);
        setNaves(n || []);
      } catch (err) { /* ignore */ }
    };
    if (rutEmpresa) cargar();
  }, [rutEmpresa]);

  // Refresh
  const recargar = useCallback(async () => {
    try {
      const data = await pescaService.obtenerLiquidacion(liq.id);
      setLiq(data);
      if (onActualizado) onActualizado(data);
    } catch (err) {
      showNotification('Error al recargar', 'error');
    }
  }, [liq.id, onActualizado, showNotification]);

  // Dialog helpers
  const abrirDialogCaptura = (data = null) => {
    setDialog({
      open: true, tipo: 'captura', editId: data?.id || null,
      data: data || { fecha: '', producto: '', tonelaje: '', precio_tonelada: '', monto: '', guia_despacho: '' },
    });
  };

  const abrirDialogGasto = (data = null) => {
    setDialog({
      open: true, tipo: 'gasto', editId: data?.id || null,
      data: data || { fecha: '', proveedor: '', detalle: '', numero_documento: '', monto: '', tipo: 'OTRO' },
    });
  };

  const abrirDialogViaje = (data = null) => {
    setDialog({
      open: true, tipo: 'viaje', editId: data?.id || null,
      data: data || { numero_parte: '', fecha: '', tonelaje_total: '', tripulacion: [] },
    });
  };

  const cerrarDialog = () => {
    setDialog({ open: false, tipo: null, data: null, editId: null });
  };

  const handleFieldChange = (field, value) => {
    setDialog(prev => ({ ...prev, data: { ...prev.data, [field]: value } }));
  };

  // Save handlers
  const guardarCaptura = async () => {
    setGuardando(true);
    try {
      const payload = {
        fecha: dialog.data.fecha || null,
        producto: dialog.data.producto || null,
        tonelaje: parseFloat(dialog.data.tonelaje) || 0,
        precio_tonelada: parseFloat(dialog.data.precio_tonelada) || 0,
        monto: parseFloat(dialog.data.monto) || 0,
        guia_despacho: dialog.data.guia_despacho || null,
      };
      if (dialog.editId) {
        await pescaService.editarCaptura(liq.id, dialog.editId, payload);
      } else {
        await pescaService.agregarCaptura(liq.id, payload);
      }
      showNotification('Captura guardada', 'success');
      cerrarDialog();
      recargar();
    } catch (err) {
      showNotification(`Error: ${err.message || err}`, 'error');
    } finally {
      setGuardando(false);
    }
  };

  const guardarGasto = async () => {
    setGuardando(true);
    try {
      const payload = {
        fecha: dialog.data.fecha || null,
        proveedor: dialog.data.proveedor || null,
        detalle: dialog.data.detalle || null,
        numero_documento: dialog.data.numero_documento || null,
        monto: parseFloat(dialog.data.monto) || 0,
        tipo: dialog.data.tipo || 'OTRO',
      };
      if (dialog.editId) {
        await pescaService.editarGasto(liq.id, dialog.editId, payload);
      } else {
        await pescaService.agregarGasto(liq.id, payload);
      }
      showNotification('Gasto guardado', 'success');
      cerrarDialog();
      recargar();
    } catch (err) {
      showNotification(`Error: ${err.message || err}`, 'error');
    } finally {
      setGuardando(false);
    }
  };

  const guardarViaje = async () => {
    setGuardando(true);
    try {
      const payload = {
        numero_parte: parseInt(dialog.data.numero_parte) || 1,
        fecha: dialog.data.fecha || null,
        tonelaje_total: parseFloat(dialog.data.tonelaje_total) || 0,
        tripulacion: (dialog.data.tripulacion || []).map(t => ({
          tripulante_id: parseInt(t.tripulante_id),
          cargo_id: parseInt(t.cargo_id),
          factor_participacion: parseFloat(t.factor_participacion) || 1.0,
        })),
      };
      if (dialog.editId) {
        await pescaService.editarViaje(liq.id, dialog.editId, payload);
      } else {
        await pescaService.agregarViaje(liq.id, payload);
      }
      showNotification('Viaje guardado', 'success');
      cerrarDialog();
      recargar();
    } catch (err) {
      showNotification(`Error: ${err.message || err}`, 'error');
    } finally {
      setGuardando(false);
    }
  };

  const handleGuardar = () => {
    if (dialog.tipo === 'captura') guardarCaptura();
    else if (dialog.tipo === 'gasto') guardarGasto();
    else if (dialog.tipo === 'viaje') guardarViaje();
  };

  // Delete
  const handleEliminar = async () => {
    setEliminando(true);
    try {
      const { tipo, id } = deleteConfirm;
      if (tipo === 'captura') await pescaService.eliminarCaptura(liq.id, id);
      else if (tipo === 'gasto') await pescaService.eliminarGasto(liq.id, id);
      else if (tipo === 'viaje') await pescaService.eliminarViaje(liq.id, id);
      showNotification('Eliminado', 'success');
      setDeleteConfirm({ open: false, tipo: null, id: null });
      recargar();
    } catch (err) {
      showNotification(`Error: ${err.message || err}`, 'error');
    } finally {
      setEliminando(false);
    }
  };

  // Calculate
  const handleCalcular = async () => {
    setCalculando(true);
    try {
      const data = await pescaService.calcularDistribucion(liq.id);
      setLiq(data);
      if (onActualizado) onActualizado(data);
      showNotification('Distribución calculada', 'success');
    } catch (err) {
      showNotification(`Error: ${err.message || err}`, 'error');
    } finally {
      setCalculando(false);
    }
  };

  // Liquidar
  const handleLiquidar = async () => {
    setLiquidando(true);
    try {
      await pescaService.liquidar(liq.id);
      recargar();
      showNotification('Liquidación cerrada', 'success');
    } catch (err) {
      showNotification(`Error: ${err.detail || err.message || err}`, 'error');
    } finally {
      setLiquidando(false);
    }
  };

  // PDF download
  const handleDescargarPDF = async (tipo) => {
    setPdfAnchor(null);
    setDescargandoPdf(true);
    try {
      const blob = await pescaService.descargarPDF(liq.id, tipo);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `liquidacion_${liq.numero}_${tipo}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showNotification('PDF descargado', 'success');
    } catch (err) {
      showNotification(`Error al generar PDF: ${err.message || err}`, 'error');
    } finally {
      setDescargandoPdf(false);
    }
  };

  // Excel download
  const handleDescargarExcel = async () => {
    setDescargandoExcel(true);
    try {
      const blob = await pescaService.descargarExcel(liq.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `liquidacion_${liq.numero}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showNotification('Excel descargado', 'success');
    } catch (err) {
      showNotification(`Error al generar Excel: ${err.message || err}`, 'error');
    } finally {
      setDescargandoExcel(false);
    }
  };

  // Descuentos handlers
  const abrirDescDialog = (dist) => {
    setDescData({
      anticipos: dist.anticipos || 0,
      seguros: dist.seguros || 0,
      prestamos: dist.prestamos || 0,
    });
    setDescDialog({ open: true, dist });
  };

  const guardarDescuentos = async () => {
    setGuardandoDesc(true);
    try {
      await pescaService.actualizarDistribucion(liq.id, descDialog.dist.id, descData);
      showNotification('Descuentos actualizados', 'success');
      setDescDialog({ open: false, dist: null });
      // Recalcular para actualizar líquidos
      await handleCalcular();
    } catch (err) {
      showNotification(`Error: ${err.message || err}`, 'error');
    } finally {
      setGuardandoDesc(false);
    }
  };

  // Tripulacion helpers for viaje dialog
  const addTripulante = () => {
    const trip = dialog.data.tripulacion || [];
    handleFieldChange('tripulacion', [...trip, { tripulante_id: '', cargo_id: '', factor_participacion: 1.0 }]);
  };

  const updateTripulante = (idx, field, value) => {
    const trip = [...(dialog.data.tripulacion || [])];
    trip[idx] = { ...trip[idx], [field]: value };
    // Auto-set factor when cargo changes
    if (field === 'cargo_id') {
      const cargo = cargos.find(c => c.id === parseInt(value));
      if (cargo) trip[idx].factor_participacion = cargo.factor_participacion;
    }
    handleFieldChange('tripulacion', trip);
  };

  const removeTripulante = (idx) => {
    const trip = [...(dialog.data.tripulacion || [])];
    trip.splice(idx, 1);
    handleFieldChange('tripulacion', trip);
  };

  // Paste from Excel
  const abrirPasteDialog = (tipo) => {
    setPasteDialog({ open: true, tipo });
    setPasteText('');
    setPastePreview([]);
  };

  const parsearFecha = (val) => {
    if (!val) return null;
    const s = val.trim();
    // Ya es ISO: 2026-01-15
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    // DD/MM/YYYY o DD-MM-YYYY
    const match = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (match) return `${match[3]}-${match[2].padStart(2, '0')}-${match[1].padStart(2, '0')}`;
    // DD/MM/YY
    const match2 = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2})$/);
    if (match2) return `20${match2[3]}-${match2[2].padStart(2, '0')}-${match2[1].padStart(2, '0')}`;
    return null;
  };

  const parsearMonto = (val) => {
    if (!val) return 0;
    return parseFloat(val.toString().replace(/[$.]/g, '').replace(',', '.')) || 0;
  };

  const parsearPegado = (text, tipo) => {
    const lines = text.trim().split('\n').filter(l => l.trim());
    const parsed = [];
    for (const line of lines) {
      const cols = line.split('\t');
      if (tipo === 'gasto') {
        // Esperado: Fecha | Proveedor | Detalle | N°Doc | Monto | Tipo
        const monto = parsearMonto(cols[4]) || parsearMonto(cols[3]);
        if (!monto) continue;
        parsed.push({
          fecha: parsearFecha(cols[0]),
          proveedor: cols[1]?.trim() || null,
          detalle: cols[2]?.trim() || null,
          numero_documento: cols[3]?.trim() || null,
          monto,
          tipo: cols[5]?.trim()?.toUpperCase() || 'OTRO',
        });
      } else if (tipo === 'captura') {
        // Esperado: Fecha | Producto | Tonelaje | Precio/Ton | Monto | Guía
        const tonelaje = parsearMonto(cols[2]);
        const precio = parsearMonto(cols[3]);
        const monto = parsearMonto(cols[4]);
        if (!tonelaje && !monto) continue;
        parsed.push({
          fecha: parsearFecha(cols[0]),
          producto: cols[1]?.trim() || null,
          tonelaje,
          precio_tonelada: precio,
          monto: monto || tonelaje * precio,
          guia_despacho: cols[5]?.trim() || null,
        });
      }
    }
    return parsed;
  };

  const handlePasteTextChange = (text) => {
    setPasteText(text);
    if (text.trim()) {
      setPastePreview(parsearPegado(text, pasteDialog.tipo));
    } else {
      setPastePreview([]);
    }
  };

  const guardarPegados = async () => {
    if (pastePreview.length === 0) return;
    setPegando(true);
    try {
      let errores = 0;
      for (const item of pastePreview) {
        try {
          if (pasteDialog.tipo === 'gasto') {
            await pescaService.agregarGasto(liq.id, item);
          } else if (pasteDialog.tipo === 'captura') {
            await pescaService.agregarCaptura(liq.id, item);
          }
        } catch { errores++; }
      }
      const total = pastePreview.length - errores;
      showNotification(`${total} ${pasteDialog.tipo === 'gasto' ? 'gastos' : 'capturas'} agregados`, 'success');
      setPasteDialog({ open: false, tipo: null });
      recargar();
    } catch (err) {
      showNotification(`Error: ${err.message || err}`, 'error');
    } finally {
      setPegando(false);
    }
  };

  // Column definitions
  const naveName = naves.find(n => n.id === liq.nave_id)?.nombre || '-';

  const colCapturas = [
    { field: 'fecha', headerName: 'Fecha', minWidth: 100, renderCell: formatDate },
    { field: 'producto', headerName: 'Producto', minWidth: 140 },
    { field: 'tonelaje', headerName: 'Tonelaje', minWidth: 100, align: 'right', renderCell: (v) => v?.toFixed(3) },
    { field: 'precio_tonelada', headerName: 'Precio/Ton', minWidth: 120, align: 'right', renderCell: formatMoney },
    { field: 'monto', headerName: 'Monto', minWidth: 120, align: 'right', renderCell: formatMoney },
    { field: 'guia_despacho', headerName: 'Guía', minWidth: 100 },
  ];

  const colGastos = [
    { field: 'fecha', headerName: 'Fecha', minWidth: 100, renderCell: formatDate },
    { field: 'proveedor', headerName: 'Proveedor', minWidth: 150 },
    { field: 'detalle', headerName: 'Detalle', minWidth: 200 },
    { field: 'numero_documento', headerName: 'Documento', minWidth: 120 },
    { field: 'monto', headerName: 'Monto', minWidth: 120, align: 'right', renderCell: formatMoney },
    { field: 'tipo', headerName: 'Tipo', minWidth: 100, renderCell: (v) => <Chip label={v} size="small" variant="outlined" /> },
  ];

  const colViajes = [
    { field: 'numero_parte', headerName: 'Parte N°', minWidth: 80 },
    { field: 'fecha', headerName: 'Fecha', minWidth: 100, renderCell: formatDate },
    { field: 'tonelaje_total', headerName: 'Tonelaje', minWidth: 100, align: 'right', renderCell: (v) => v?.toFixed(3) },
    { field: 'tripulacion', headerName: 'Tripulantes', minWidth: 100, renderCell: (val) => val?.length || 0 },
  ];

  const colDistribucion = [
    { field: 'tripulante_id', headerName: 'Tripulante', minWidth: 180, renderCell: (val) => {
      const t = tripulantes.find(tr => tr.id === val);
      return t ? `${t.nombre} (${t.rut}-${t.dv})` : `ID ${val}`;
    }},
    { field: 'monto_calculado', headerName: 'Calculado', minWidth: 110, align: 'right', renderCell: formatMoney },
    { field: 'parte_armador', headerName: 'Armador', minWidth: 100, align: 'right', renderCell: formatMoney },
    { field: 'monto_bruto', headerName: 'Bruto', minWidth: 110, align: 'right', renderCell: formatMoney },
    { field: 'retencion', headerName: 'Retención', minWidth: 100, align: 'right', renderCell: formatMoney },
    { field: 'anticipos', headerName: 'Anticipos', minWidth: 100, align: 'right', renderCell: formatMoney },
    { field: 'seguros', headerName: 'Seguros', minWidth: 90, align: 'right', renderCell: formatMoney },
    { field: 'prestamos', headerName: 'Préstamos', minWidth: 90, align: 'right', renderCell: formatMoney },
    { field: 'monto_liquido', headerName: 'Líquido', minWidth: 120, align: 'right', renderCell: formatMoney },
  ];

  const distActions = esBorrador ? [
    { tooltip: 'Editar descuentos', icon: <EditIcon fontSize="small" />, onClick: (row) => abrirDescDialog(row) },
  ] : [];

  const editDeleteActions = (tipo) => esBorrador ? [
    { tooltip: 'Editar', icon: <EditIcon fontSize="small" />, onClick: (row) => {
      if (tipo === 'captura') abrirDialogCaptura(row);
      else if (tipo === 'gasto') abrirDialogGasto(row);
      else if (tipo === 'viaje') abrirDialogViaje(row);
    }},
    { tooltip: 'Eliminar', icon: <DeleteIcon fontSize="small" color="error" />, onClick: (row) => setDeleteConfirm({ open: true, tipo, id: row.id }) },
  ] : [];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Button startIcon={<BackIcon />} onClick={onVolver}>Volver</Button>
        <Typography variant="h6">Liquidación N°{liq.numero}</Typography>
        <Chip label={liq.estado} color={estadoColor[liq.estado] || 'default'} />
        <Box sx={{ flexGrow: 1 }} />
        {esBorrador && (
          <>
            <Button variant="outlined" startIcon={calculando ? <CircularProgress size={16} /> : <CalcIcon />}
              onClick={handleCalcular} disabled={calculando}>
              Calcular
            </Button>
            <Button variant="contained" color="success"
              startIcon={liquidando ? <CircularProgress size={16} /> : <LiquidarIcon />}
              onClick={handleLiquidar} disabled={liquidando}>
              Liquidar
            </Button>
          </>
        )}
        <Tooltip title="Descargar PDF">
          <IconButton onClick={(e) => setPdfAnchor(e.currentTarget)} disabled={descargandoPdf}>
            {descargandoPdf ? <CircularProgress size={20} /> : <PdfIcon />}
          </IconButton>
        </Tooltip>
        <Menu anchorEl={pdfAnchor} open={Boolean(pdfAnchor)} onClose={() => setPdfAnchor(null)}>
          <MenuItem onClick={() => handleDescargarPDF('liquidacion')}>
            <ListItemIcon><DocIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Liquidaci&oacute;n</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleDescargarPDF('distribucion')}>
            <ListItemIcon><DocIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Distribuci&oacute;n</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleDescargarPDF('viajes')}>
            <ListItemIcon><ViajesIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Viajes</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleDescargarPDF('recibo')}>
            <ListItemIcon><ReceiptIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Recibo</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleDescargarPDF('completo')}>
            <ListItemIcon><AllIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Completo (todo)</ListItemText>
          </MenuItem>
        </Menu>
        <Tooltip title="Exportar a Excel">
          <IconButton onClick={handleDescargarExcel} disabled={descargandoExcel} color="success">
            {descargandoExcel ? <CircularProgress size={20} /> : <ExcelIcon />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Recargar"><IconButton onClick={recargar}><RefreshIcon /></IconButton></Tooltip>
      </Box>

      {/* Summary Cards */}
      <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, rowGap: 2 }}>
          <Box sx={{ minWidth: 140 }}>
            <Typography variant="caption" color="text.secondary" display="block">Nave</Typography>
            <Typography variant="body2" fontWeight="bold">{naveName}</Typography>
          </Box>
          <Box sx={{ minWidth: 80 }}>
            <Typography variant="caption" color="text.secondary" display="block">Semana</Typography>
            <Typography variant="body2">{liq.semana || '-'}</Typography>
          </Box>
          <Box sx={{ minWidth: 100 }}>
            <Typography variant="caption" color="text.secondary" display="block">% Tripulación</Typography>
            <Typography variant="body2">{((liq.porcentaje_tripulacion || 0) * 100).toFixed(0)}%</Typography>
          </Box>
          <Box sx={{ minWidth: 120 }}>
            <Typography variant="caption" color="text.secondary" display="block">Ingreso Bruto</Typography>
            <Typography variant="body2" fontWeight="bold" color="success.main">{formatMoney(liq.ingreso_bruto)}</Typography>
          </Box>
          <Box sx={{ minWidth: 120 }}>
            <Typography variant="caption" color="text.secondary" display="block">Descuentos</Typography>
            <Typography variant="body2" color="error.main">{formatMoney(liq.total_descuentos)}</Typography>
          </Box>
          <Box sx={{ minWidth: 130 }}>
            <Typography variant="caption" color="text.secondary" display="block">Total Liquidación</Typography>
            <Typography variant="body2" fontWeight="bold">{formatMoney(liq.total_liquidacion)}</Typography>
          </Box>
        </Box>
      </Paper>

      {/* Tabs */}
      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tab label={`Capturas (${liq.capturas?.length || 0})`} />
        <Tab label={`Gastos (${liq.gastos?.length || 0})`} />
        <Tab label={`Viajes (${liq.viajes?.length || 0})`} />
        <Tab label={`Distribución (${liq.distribucion?.length || 0})`} />
      </Tabs>

      {/* Capturas Tab */}
      <TabPanel value={tabValue} index={0}>
        {esBorrador && (
          <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button size="small" variant="outlined" startIcon={<PasteIcon />} onClick={() => abrirPasteDialog('captura')}>
              Pegar desde Excel
            </Button>
            <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => abrirDialogCaptura()}>
              Agregar Captura
            </Button>
          </Box>
        )}
        <DataTable columns={colCapturas} data={liq.capturas || []} actions={editDeleteActions('captura')} pagination={false} />
        {(liq.capturas?.length || 0) > 0 && (
          <Box sx={{ textAlign: 'right', mt: 1 }}>
            <Typography variant="subtitle2">Total: {formatMoney(liq.capturas.reduce((s, c) => s + (c.monto || 0), 0))}</Typography>
          </Box>
        )}
      </TabPanel>

      {/* Gastos Tab */}
      <TabPanel value={tabValue} index={1}>
        {esBorrador && (
          <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button size="small" variant="outlined" startIcon={<PasteIcon />} onClick={() => abrirPasteDialog('gasto')}>
              Pegar desde Excel
            </Button>
            <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => abrirDialogGasto()}>
              Agregar Gasto
            </Button>
          </Box>
        )}
        <DataTable columns={colGastos} data={liq.gastos || []} actions={editDeleteActions('gasto')} pagination={false} />
        {(liq.gastos?.length || 0) > 0 && (
          <Box sx={{ textAlign: 'right', mt: 1 }}>
            <Typography variant="subtitle2">Total: {formatMoney(liq.gastos.reduce((s, g) => s + (g.monto || 0), 0))}</Typography>
          </Box>
        )}
      </TabPanel>

      {/* Viajes Tab */}
      <TabPanel value={tabValue} index={2}>
        {esBorrador && (
          <Box sx={{ mb: 1, textAlign: 'right' }}>
            <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => abrirDialogViaje()}>
              Agregar Viaje
            </Button>
          </Box>
        )}
        <DataTable columns={colViajes} data={liq.viajes || []} actions={editDeleteActions('viaje')} pagination={false} />
      </TabPanel>

      {/* Distribucion Tab */}
      <TabPanel value={tabValue} index={3}>
        {(liq.distribucion?.length || 0) === 0 ? (
          <Alert severity="info" sx={{ mt: 1 }}>
            Presione "Calcular" para generar la distribución entre la tripulación.
          </Alert>
        ) : (
          <>
            {esBorrador && (
              <Alert severity="info" variant="outlined" sx={{ mb: 1 }}>
                Use el botón de editar para cargar anticipos, seguros y préstamos por tripulante. Luego presione "Calcular" para actualizar los montos líquidos.
              </Alert>
            )}
            <DataTable columns={colDistribucion} data={liq.distribucion || []} actions={distActions} pagination={false} />
            <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, rowGap: 2 }}>
                <Box sx={{ minWidth: 130 }}>
                  <Typography variant="caption" color="text.secondary" display="block">Monto Tripulación</Typography>
                  <Typography variant="body1" fontWeight="bold">{formatMoney(liq.monto_tripulacion)}</Typography>
                </Box>
                <Box sx={{ minWidth: 120 }}>
                  <Typography variant="caption" color="text.secondary" display="block">Total Retención</Typography>
                  <Typography variant="body1">{formatMoney(liq.total_retencion)}</Typography>
                </Box>
                <Box sx={{ minWidth: 130 }}>
                  <Typography variant="caption" color="text.secondary" display="block">Parte Armador Cap.</Typography>
                  <Typography variant="body1">{formatMoney(liq.parte_armador_capitan)}</Typography>
                </Box>
                <Box sx={{ minWidth: 120 }}>
                  <Typography variant="caption" color="text.secondary" display="block">Total Anticipos</Typography>
                  <Typography variant="body1">{formatMoney(liq.total_anticipos)}</Typography>
                </Box>
                <Box sx={{ minWidth: 140 }}>
                  <Typography variant="caption" color="text.secondary" display="block">Total Liquidación</Typography>
                  <Typography variant="h6" fontWeight="bold" color="primary.main">{formatMoney(liq.total_liquidacion)}</Typography>
                </Box>
              </Box>
            </Paper>
          </>
        )}
      </TabPanel>

      {/* Edit/Create Dialog */}
      <Dialog open={dialog.open} onClose={cerrarDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialog.editId ? 'Editar' : 'Agregar'} {dialog.tipo === 'captura' ? 'Captura' : dialog.tipo === 'gasto' ? 'Gasto' : 'Viaje'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Captura Form */}
            {dialog.tipo === 'captura' && (
              <>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <TextField label="Fecha" type="date" fullWidth size="small"
                      value={dialog.data?.fecha ? formatDate(dialog.data.fecha) : ''}
                      onChange={(e) => handleFieldChange('fecha', e.target.value)}
                      slotProps={{ inputLabel: { shrink: true } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextField label="Producto" fullWidth size="small"
                      value={dialog.data?.producto || ''}
                      onChange={(e) => handleFieldChange('producto', e.target.value)}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 4 }}>
                    <TextField label="Tonelaje" type="number" fullWidth size="small"
                      value={dialog.data?.tonelaje || ''}
                      onChange={(e) => handleFieldChange('tonelaje', e.target.value)}
                      inputProps={{ step: 0.001 }}
                    />
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <TextField label="Precio/Tonelada" type="number" fullWidth size="small"
                      value={dialog.data?.precio_tonelada || ''}
                      onChange={(e) => handleFieldChange('precio_tonelada', e.target.value)}
                    />
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <TextField label="Monto" type="number" fullWidth size="small"
                      value={dialog.data?.monto || ''}
                      onChange={(e) => handleFieldChange('monto', e.target.value)}
                      helperText="Dejar en 0 para calcular auto"
                    />
                  </Grid>
                </Grid>
                <TextField label="Guía de Despacho" fullWidth size="small"
                  value={dialog.data?.guia_despacho || ''}
                  onChange={(e) => handleFieldChange('guia_despacho', e.target.value)}
                />
              </>
            )}

            {/* Gasto Form */}
            {dialog.tipo === 'gasto' && (
              <>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <TextField label="Fecha" type="date" fullWidth size="small"
                      value={dialog.data?.fecha ? formatDate(dialog.data.fecha) : ''}
                      onChange={(e) => handleFieldChange('fecha', e.target.value)}
                      slotProps={{ inputLabel: { shrink: true } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextField label="Tipo" select fullWidth size="small"
                      value={dialog.data?.tipo || 'OTRO'}
                      onChange={(e) => handleFieldChange('tipo', e.target.value)}
                      SelectProps={{ native: true }}
                    >
                      {['FACTURA', 'BOLETA', 'SIN_BOLETA', 'OPERACION', 'CERTIFICACION', 'OTRO'].map(t =>
                        <option key={t} value={t}>{t}</option>
                      )}
                    </TextField>
                  </Grid>
                </Grid>
                <TextField label="Proveedor" fullWidth size="small"
                  value={dialog.data?.proveedor || ''}
                  onChange={(e) => handleFieldChange('proveedor', e.target.value)}
                />
                <TextField label="Detalle" fullWidth size="small"
                  value={dialog.data?.detalle || ''}
                  onChange={(e) => handleFieldChange('detalle', e.target.value)}
                />
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <TextField label="N° Documento" fullWidth size="small"
                      value={dialog.data?.numero_documento || ''}
                      onChange={(e) => handleFieldChange('numero_documento', e.target.value)}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextField label="Monto" type="number" fullWidth size="small" required
                      value={dialog.data?.monto || ''}
                      onChange={(e) => handleFieldChange('monto', e.target.value)}
                    />
                  </Grid>
                </Grid>
              </>
            )}

            {/* Viaje Form */}
            {dialog.tipo === 'viaje' && (
              <>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 4 }}>
                    <TextField label="N° Parte" type="number" fullWidth size="small" required
                      value={dialog.data?.numero_parte || ''}
                      onChange={(e) => handleFieldChange('numero_parte', e.target.value)}
                    />
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <TextField label="Fecha" type="date" fullWidth size="small"
                      value={dialog.data?.fecha ? formatDate(dialog.data.fecha) : ''}
                      onChange={(e) => handleFieldChange('fecha', e.target.value)}
                      slotProps={{ inputLabel: { shrink: true } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <TextField label="Tonelaje Total" type="number" fullWidth size="small"
                      value={dialog.data?.tonelaje_total || ''}
                      onChange={(e) => handleFieldChange('tonelaje_total', e.target.value)}
                      inputProps={{ step: 0.001 }}
                    />
                  </Grid>
                </Grid>

                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle2">Tripulación</Typography>
                  <Button size="small" startIcon={<AddIcon />} onClick={addTripulante}>
                    Agregar
                  </Button>
                </Box>

                {(dialog.data?.tripulacion || []).map((t, idx) => (
                  <Grid container spacing={1} key={idx} alignItems="center">
                    <Grid size={{ xs: 4 }}>
                      <TextField label="Tripulante" select fullWidth size="small"
                        value={t.tripulante_id || ''}
                        onChange={(e) => updateTripulante(idx, 'tripulante_id', e.target.value)}
                        SelectProps={{ native: true }}
                      >
                        <option value="">-- Seleccione --</option>
                        {tripulantes.filter(tr => tr.activo).map(tr =>
                          <option key={tr.id} value={tr.id}>{tr.nombre}</option>
                        )}
                      </TextField>
                    </Grid>
                    <Grid size={{ xs: 3 }}>
                      <TextField label="Cargo" select fullWidth size="small"
                        value={t.cargo_id || ''}
                        onChange={(e) => updateTripulante(idx, 'cargo_id', e.target.value)}
                        SelectProps={{ native: true }}
                      >
                        <option value="">-- Cargo --</option>
                        {cargos.map(c =>
                          <option key={c.id} value={c.id}>{c.nombre}</option>
                        )}
                      </TextField>
                    </Grid>
                    <Grid size={{ xs: 3 }}>
                      <TextField label="Factor" type="number" fullWidth size="small"
                        value={t.factor_participacion ?? 1.0}
                        onChange={(e) => updateTripulante(idx, 'factor_participacion', e.target.value)}
                        inputProps={{ step: 0.25, min: 0.25 }}
                      />
                    </Grid>
                    <Grid size={{ xs: 2 }}>
                      <IconButton size="small" color="error" onClick={() => removeTripulante(idx)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}
                {(dialog.data?.tripulacion || []).length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    Sin tripulación asignada. Presione "Agregar" para añadir tripulantes.
                  </Typography>
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={cerrarDialog} disabled={guardando}>Cancelar</Button>
          <Button onClick={handleGuardar} variant="contained" disabled={guardando}
            startIcon={guardando ? <CircularProgress size={16} /> : null}>
            {guardando ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <ConfirmDialog
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, tipo: null, id: null })}
        onConfirm={handleEliminar}
        title="Confirmar eliminación"
        message={`¿Eliminar ${deleteConfirm.tipo === 'captura' ? 'esta captura' : deleteConfirm.tipo === 'gasto' ? 'este gasto' : 'este viaje'}?`}
        confirmText="Eliminar"
        loading={eliminando}
        severity="error"
      />

      {/* Descuentos Dialog */}
      <Dialog open={descDialog.open} onClose={() => setDescDialog({ open: false, dist: null })} maxWidth="xs" fullWidth>
        <DialogTitle>
          Descuentos - {descDialog.dist ? (() => {
            const t = tripulantes.find(tr => tr.id === descDialog.dist.tripulante_id);
            return t ? t.nombre : `Tripulante ID ${descDialog.dist.tripulante_id}`;
          })() : ''}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="Anticipos" type="number" fullWidth size="small"
              value={descData.anticipos}
              onChange={(e) => setDescData(prev => ({ ...prev, anticipos: parseFloat(e.target.value) || 0 }))}
            />
            <TextField label="Seguros" type="number" fullWidth size="small"
              value={descData.seguros}
              onChange={(e) => setDescData(prev => ({ ...prev, seguros: parseFloat(e.target.value) || 0 }))}
            />
            <TextField label="Préstamos" type="number" fullWidth size="small"
              value={descData.prestamos}
              onChange={(e) => setDescData(prev => ({ ...prev, prestamos: parseFloat(e.target.value) || 0 }))}
            />
            {descDialog.dist && (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="caption" color="text.secondary" display="block">Bruto: {formatMoney(descDialog.dist.monto_bruto)}</Typography>
                <Typography variant="caption" color="text.secondary" display="block">Retención: {formatMoney(descDialog.dist.retencion)}</Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Líquido estimado: {formatMoney(
                    (descDialog.dist.monto_bruto || 0) - (descDialog.dist.retencion || 0)
                    - (descData.anticipos || 0) - (descData.seguros || 0) - (descData.prestamos || 0)
                  )}
                </Typography>
              </Paper>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDescDialog({ open: false, dist: null })} disabled={guardandoDesc}>Cancelar</Button>
          <Button onClick={guardarDescuentos} variant="contained" disabled={guardandoDesc}
            startIcon={guardandoDesc ? <CircularProgress size={16} /> : null}>
            {guardandoDesc ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Paste from Excel Dialog */}
      <Dialog open={pasteDialog.open} onClose={() => setPasteDialog({ open: false, tipo: null })} maxWidth="md" fullWidth>
        <DialogTitle>
          Pegar {pasteDialog.tipo === 'gasto' ? 'Gastos' : 'Capturas'} desde Excel
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="info" variant="outlined">
              {pasteDialog.tipo === 'gasto'
                ? 'Copie las filas desde Excel con columnas: Fecha | Proveedor | Detalle | N° Documento | Monto | Tipo'
                : 'Copie las filas desde Excel con columnas: Fecha | Producto | Tonelaje | Precio/Ton | Monto | Guía'}
            </Alert>
            <TextField
              label="Pegue aquí los datos copiados de Excel"
              multiline rows={8} fullWidth
              value={pasteText}
              onChange={(e) => handlePasteTextChange(e.target.value)}
              placeholder={pasteDialog.tipo === 'gasto'
                ? '01/01/2026\tProveedor\tDetalle\tFAC.123\t50000\tFACTURA'
                : '01/01/2026\tSardina\t15.892\t170000\t2701640\t12345'}
            />
            {pastePreview.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Vista previa: {pastePreview.length} {pasteDialog.tipo === 'gasto' ? 'gastos' : 'capturas'} detectados
                </Typography>
                <DataTable
                  columns={pasteDialog.tipo === 'gasto' ? colGastos : colCapturas}
                  data={pastePreview.map((item, i) => ({ ...item, id: i }))}
                  pagination={false}
                />
              </Box>
            )}
            {pasteText.trim() && pastePreview.length === 0 && (
              <Alert severity="warning">No se detectaron datos válidos. Verifique el formato.</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasteDialog({ open: false, tipo: null })}>Cancelar</Button>
          <Button onClick={guardarPegados} variant="contained" disabled={pastePreview.length === 0 || pegando}
            startIcon={pegando ? <CircularProgress size={16} /> : null}>
            {pegando ? 'Guardando...' : `Agregar ${pastePreview.length} registros`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LiquidacionDetalle;
